import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import SensorTypeModel from 'src/app/services/sensorType.model';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';
import { SensorTypeService } from 'src/app/services/sensor-type.service';

declare var $: any;

@Component({
  selector: 'app-sensor-types',
  templateUrl: './sensor-types.component.html',
  styleUrls: ['./sensor-types.component.scss']
})
export class SensorTypesComponent implements OnInit, OnDestroy {
  public sensorTypes: SensorTypeModel[] = [];
  public sensorTypesList: SensorTypeModel[] = [];
  public sensorType: SensorTypeModel = new SensorTypeModel();
  public filteredSensorTypes: SensorTypeModel[] = [];
  public search: string = '';
  public submitButton: boolean = false;
  public updateButton: boolean = false;
  public sensorTypeDoubleClick: boolean = false;
  public NoDataMessage: boolean = false;
  public clearSearchInput: boolean = false;
  public registerForm: FormGroup;
  public submitted = false;
  public listLoader = false;
  public startIndex = 0;
  public isFiltering: boolean = false;
  public noList: boolean = false;
  public enableDisablePageLoader: boolean = false;
  searchTimeout: any;
  private scrollTimeout: any;
  public formFieldCount: number = 0;
  public showForm: boolean = false;

  private batchLoader: BatchLoader<any>;

  constructor(
    private toastrService: ToastrService,
    public formBuilder: FormBuilder,
    public sensorTypeService: SensorTypeService,
    private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService
  ) {
    this.sensorType = new SensorTypeModel();
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.search = '';
    this.sensorTypes = [];
    this.sensorTypesList = [];
    this.filteredSensorTypes = [];
    this.isFiltering = false;
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.sensorTypes = await this.sensorTypeService.getAllSensorTypes();
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredSensorTypes : this.sensorTypes,
      (updatedList) => {
        this.sensorTypesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  get f() {
    return this.registerForm.controls;
  }

  public ngOnDestroy(): void {
    this.sensorTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.sensorTypes.filter(route =>
          route.name?.toLowerCase().includes(query) || route.abbreviation?.toLowerCase().includes(query)
        )
      : this.sensorTypes;
    this.filteredSensorTypes = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      this.noList = true;
      this.sensorTypesList = [];
      return;
    }
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.sensorType = new SensorTypeModel();
    this.sensorTypeDoubleClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  public async addUpdateSensorType() {
    this.submitted = true;
    if (this.registerForm.invalid) {
      return;
    }
    const formData = this.registerForm.value;
    try {
      this.sensorTypeDoubleClick = true;
      if (this.submitButton) {
        await this.sensorTypeService.create(formData);
        this.toastrService.success('Sensor type created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateSensorType = { ...formData };
        delete updateSensorType.id;
        await this.sensorTypeService.updateOneByID(this.sensorType.id, updateSensorType);
        this.toastrService.success('Sensor type updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.sensorTypeDoubleClick = false;
    }
  }

  public getSensorTypeById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.sensorTypeDoubleClick = false;
    this.updateButton = true;
    const foundSensorType = this.sensorTypesList.find(uom => uom?.id === id);
    this.sensorType = foundSensorType ? { ...foundSensorType } : new SensorTypeModel();
    if (this.sensorType) {
      this.registerForm.patchValue({
        name: this.sensorType.name,
        abbreviation: this.sensorType.abbreviation,
        description: this.sensorType.description
      });
    }
  }

  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}

